import type { MaybeRefOrGetter } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import {
  useAssignResultTemplateToProgrammes,
  useSetProgrammesResultTemplateDefault,
} from '@/composables/mutations/tertiary/useProgrammeResultTemplateMutations';
import { extractGraphQLError } from '@/utils/graphqlError';
import {
  APPLY_MODE,
  assignmentSummaryMessage,
  assignmentValidationMessage,
  buildAssignProgrammesInput,
  buildHistoricalSourceMap,
  buildSetDefaultVariables,
  buildCalendarOptions,
  getHistoricalColumns,
  programmeDefaultTemplateLabel,
  replaceButtonLabel,
  setDefaultSummaryMessage,
  submitButtonLabel,
  type ApplyMode,
  type TemplateColumnLike,
} from '@/utils/programmeTemplateAssignment';
import type { ProgrammeTemplateAssignmentConflict, ResultTemplate } from '@/types/composition-functions';

export type AssignableTemplate = Pick<ResultTemplate, 'id' | 'name'> & {
  columns?: ReadonlyArray<TemplateColumnLike | null> | null;
};

export const ASSIGN_STEP = {
  FORM: 'FORM',
  CONFIRM_REPLACE: 'CONFIRM_REPLACE',
} as const;

export type AssignStep = typeof ASSIGN_STEP[keyof typeof ASSIGN_STEP];

const ERROR_FALLBACK = 'We could not assign the template. Please try again.';

/**
 * State and actions for "Assign template to programme(s)" (tertiary).
 *
 * - All semesters: makes the template each selected programme's default.
 * - Specific semesters: one atomic bulk assignment; when some pairs already use
 *   a different template the server writes nothing and returns them, and the
 *   user confirms a replace (re-submitted with overwrite: true).
 * Templates with historical columns are per-semester only, one semester at a time.
 */
export function useAssignTemplateToProgrammes(
  template: MaybeRefOrGetter<AssignableTemplate>,
  options: { onSuccess?: () => void } = {},
) {
  const toast = useToast();

  const { academicCalendars: schoolCalendars } = useSchoolQuery();
  const { programmes, loadingSchoolProgramme: loadingProgrammes } = useProgrammes({
    autoFetch: true,
    first: 100,
    page: 1,
  });
  const { assignToProgrammes } = useAssignResultTemplateToProgrammes();
  const { setDefault } = useSetProgrammesResultTemplateDefault();

  const historicalColumns = computed(() => getHistoricalColumns(toValue(template)?.columns));
  const hasHistoricalColumns = computed(() => historicalColumns.value.length > 0);

  const mode = ref<ApplyMode>(hasHistoricalColumns.value ? APPLY_MODE.SPECIFIC : APPLY_MODE.ALL);
  const programmeIds = ref<string[]>([]);
  const calendarIds = ref<string[]>([]);
  const historicalMapping = ref<Record<string, string>>({});
  const step = ref<AssignStep>(ASSIGN_STEP.FORM);
  const conflicts = ref<ProgrammeTemplateAssignmentConflict[]>([]);
  const errorMessage = ref<string | null>(null);
  const saving = ref(false);

  // A historical template can never be an all-semesters default, and only one
  // semester can be targeted per submission.
  watch(hasHistoricalColumns, (historical) => {
    if (!historical) return;
    mode.value = APPLY_MODE.SPECIFIC;
    if (calendarIds.value.length > 1) calendarIds.value = calendarIds.value.slice(0, 1);
  }, { immediate: true });

  const semesterSelectionLimit = computed(() => (hasHistoricalColumns.value ? 1 : undefined));

  const applyModeOptions = computed(() => [
    { label: 'All semesters (default)', value: APPLY_MODE.ALL, disabled: hasHistoricalColumns.value },
    { label: 'Specific semesters', value: APPLY_MODE.SPECIFIC, disabled: false },
  ]);

  const programmeOptions = computed(() => programmes.value.map((programme) => ({
    ...programme,
    defaultTemplateLabel: programmeDefaultTemplateLabel(programme),
  })));

  const selectedProgrammes = computed(() =>
    programmeOptions.value.filter((programme) => programmeIds.value.includes(programme.id)));

  const calendarOptions = computed(() => buildCalendarOptions(schoolCalendars.value ?? []));

  const validationMessage = computed(() => assignmentValidationMessage({
    mode: mode.value,
    programmeIds: programmeIds.value,
    calendarIds: calendarIds.value,
    historicalColumns: historicalColumns.value,
    historicalMapping: historicalMapping.value,
  }));
  const isValid = computed(() => validationMessage.value === null);

  const submitLabel = computed(() =>
    submitButtonLabel(mode.value, programmeIds.value.length, calendarIds.value.length));
  const replaceLabel = computed(() => replaceButtonLabel(conflicts.value.length));

  // Editing the form invalidates a pending conflict list and any old error.
  watch([mode, programmeIds, calendarIds], () => {
    errorMessage.value = null;
    conflicts.value = [];
  });

  function fail(error: unknown) {
    console.error(error);
    const message = extractGraphQLError(error, ERROR_FALLBACK);
    errorMessage.value = message;
    toast.add({ severity: 'error', summary: 'Template not assigned', detail: message, life: 6000 });
  }

  function succeed(detail: string) {
    toast.add({ severity: 'success', summary: 'Template assigned', detail, life: 4000 });
    step.value = ASSIGN_STEP.FORM;
    conflicts.value = [];
    options.onSuccess?.();
  }

  async function runSpecific(overwrite: boolean): Promise<boolean> {
    const result = await assignToProgrammes(buildAssignProgrammesInput({
      programmeIds: programmeIds.value,
      calendarIds: calendarIds.value,
      templateId: toValue(template).id,
      historicalSourceMap: buildHistoricalSourceMap(historicalColumns.value, historicalMapping.value),
      overwrite,
    }));
    if (!result) throw new Error(ERROR_FALLBACK);

    if (!result.applied) {
      conflicts.value = result.conflicts;
      step.value = ASSIGN_STEP.CONFIRM_REPLACE;
      return false;
    }
    succeed(assignmentSummaryMessage(result));
    return true;
  }

  async function submit(): Promise<boolean> {
    if (saving.value || !isValid.value) return false;
    saving.value = true;
    errorMessage.value = null;
    try {
      if (mode.value === APPLY_MODE.ALL) {
        await setDefault(buildSetDefaultVariables(programmeIds.value, toValue(template).id));
        succeed(setDefaultSummaryMessage(programmeIds.value.length));
        return true;
      }
      return await runSpecific(false);
    } catch (error) {
      fail(error);
      return false;
    } finally {
      saving.value = false;
    }
  }

  async function confirmReplace(): Promise<boolean> {
    if (saving.value) return false;
    saving.value = true;
    errorMessage.value = null;
    try {
      return await runSpecific(true);
    } catch (error) {
      fail(error);
      return false;
    } finally {
      saving.value = false;
    }
  }

  function backToForm() {
    step.value = ASSIGN_STEP.FORM;
    errorMessage.value = null;
  }

  return {
    // state
    mode,
    programmeIds,
    calendarIds,
    historicalMapping,
    step,
    conflicts,
    errorMessage,
    saving,
    // derived
    applyModeOptions,
    calendarOptions,
    hasHistoricalColumns,
    historicalColumns,
    isValid,
    loadingProgrammes,
    programmeOptions,
    replaceLabel,
    selectedProgrammes,
    semesterSelectionLimit,
    submitLabel,
    validationMessage,
    // actions
    backToForm,
    confirmReplace,
    submit,
  };
}
